<script>
import * as m from '@app/paraglide/messages.js';
import { localeState } from '@app/store/localeStore.svelte.js';
import { projectState } from "@app/store/projectStore.svelte.js";
import { eventGroupState } from "@app/store/eventGroupStore.svelte.js";
import { metricsState } from "@app/store/metricsStore.svelte.js";
import tcLib from "@cclib/lib/timecode.js";
import { decode } from "html-entities";
import convertToPlainText from "@cclib/functions/quill/convertToPlainText.js";
import {
    getTotalChars,
    calculateCps,
    calculateWpm,
    calculateDuration,
} from "@cclib/lib/styleGuideTests.js";

let selectedEvent = $derived(
    eventGroupState[projectState.selected]?.events[
        eventGroupState[projectState.selected]?.selected[0]
    ],
);

let plainText = $derived(convertToPlainText(selectedEvent?.text || ""));
let lineInfo = $derived.by(() => {
    let lines = [];
    plainText.split("\n").forEach((line) => {
        lines.push(decode(line).length);
    });

    return lines;
});
let totalWords = $derived(plainText.split(/\s/gm).length);

let totalChars = $derived.by(() => {
    if (!eventGroupState[projectState.selected]) return 0;
    // Use the imported getTotalChars function
    return getTotalChars(plainText, "text");
});

let cpsChars = $derived.by(() => {
    if (!eventGroupState[projectState.selected]) return 0;
    // Reading rate (CPS) can optionally exclude spaces based on user metrics settings
    return getTotalChars(plainText, "text", metricsState.cpsIncludeSpaces === false);
});

let duration = $derived(calculateDuration(selectedEvent.start, selectedEvent.end, 3));
let wpm = $derived(calculateWpm(totalWords, duration));
let cps = $derived(calculateCps(cpsChars, duration));

// The reading-rate bar can display either CPS or WPM based on user metrics settings
let barRate = $derived(metricsState.summaryBarRate === "wpm" ? "wpm" : "cps");
let maxCps = $derived(eventGroupState[projectState.selected]?.maxCps || 20);
let maxWpm = $derived(eventGroupState[projectState.selected]?.maxWpm || 160);
let barValue = $derived(barRate === "wpm" ? wpm : cps);
let barMax = $derived(barRate === "wpm" ? maxWpm : maxCps);
let barUnit = $derived(barRate === "wpm" ? "WPM" : "CPS");

let remainingToLimit = $derived.by(() => {
    if (!eventGroupState[projectState.selected]) return 0;
    if (barRate === "wpm") {
        let maxWordsForDuration = (maxWpm * duration) / 60;
        return Math.round(maxWordsForDuration - totalWords);
    }
    let maxCharsForDuration = maxCps * duration;
    return Math.round(maxCharsForDuration - cpsChars);
});

/* We calculate the Progress Bar widths based on the active reading rate (CPS or WPM). The progress bar is made up of three sections. The first section is the green section that represents a rate of 0 to the Event Group's set maximum. The first section has a max width of 75%. That means that when the Event matches the Event Group's max rate, the green section fills 75% of the progress bar. The second section is the yellow section that represents a rate above the max up to a certain threshold. It takes up a maximum of 5% - comfortable room for error. The third section is the red section that represents a rate above that threshold and has a width of 20%. */
let progressBarWidths = $derived.by(() => {
    let widths = {
        green: 0,
        yellow: 0,
        red: 0,
    };

    let valueMaxPercent = (barValue / (barMax || 1)) * 100;

    // Green: 0-100% → 0-75% width
    widths.green = Math.min(valueMaxPercent * 0.75, 75);

    // Yellow: 100-105% → 0-5% width (1:1 ratio)
    if (valueMaxPercent > 100) {
        widths.yellow = Math.min((valueMaxPercent - 100), 5);
    }

    // Red: 105-130% → 0-20% width (0.8 ratio)
    if (valueMaxPercent > 105) {
        widths.red = Math.min((valueMaxPercent - 105) * 0.8, 20);
    }

    return widths;
});

let locale = $derived(localeState.current);
</script>

{#key locale}
<div class="summary-container p-2">
    <!-- Event Information -->
    {#if selectedEvent}
        <div class="row g-3">
            <div class="col-12">
                <p class="mb-1 small text-muted" title={m.summary_remaining_chars_cps_title()}>{ m.summary_reading_rate_label()} <span class="pe-none float-end fw-bold {remainingToLimit >= 0 ? 'text-primary' : 'text-danger'}">{remainingToLimit}</span></p>
                <div id="ReadingRateMeter" class="progress-stacked border rounded">
                    <div
                        class="progress"
                        style="width: {progressBarWidths.green}%; transition: width 0.5s ease-in-out;">
                        <div class="progress-bar" style="background-image: linear-gradient(to right, #0fbc8c 85%, #F39C12 100%);">{barValue ? barValue.toFixed(2) : 0} {barUnit}</div>
                    </div>
                    <div id="maxReadingRateMarkerLine" title={m.summary_max_cps_title({maxCps: barMax})}></div>
                    <div
                        class="progress"
                        style="width: {progressBarWidths.yellow}%; transition: width 0.5s ease-in-out;">
                        <div class="progress-bar" style="background-image: linear-gradient(to right, #e69310 80%, #e69310 100%);"></div>
                    </div>
                    <div
                        class="progress"
                        style="width: {progressBarWidths.red}%; transition: width 0.5s ease-in-out;">
                        <div class="progress-bar" style="background-image: linear-gradient(to right, #e69310 5%, #E74C3C 100%);"></div>
                    </div>
                </div>
            </div>
            <div class="col-6 pe-none">
                <p class="mb-1 fw-bold text-capitalize">{m.summary_event_metrics_label()}</p>
                <p class="m-0 font-monospace" title={m.summary_wpm_title()}>{wpm} WPM</p>
                <p class="m-0 font-monospace" title={m.summary_cps_title()}>{cps} CPS</p>
                <p class="m-0 font-monospace" title={m.summary_duration_title()}>{duration} sec</p>
                <p class="m-0 font-monospace"><span class="fs-6 badge text-bg-light text-dark">({lineInfo && lineInfo.join(',')})={totalChars}</span></p>
            </div>
            <div class="col-6 pe-none">
                <p class="mb-1 fw-bold text-capitalize">{m.summary_timing_info_label()}</p>
                <p class="small text-muted mb-0">{m.summary_seconds_label()}: <span class="font-monospace">{selectedEvent.start ? selectedEvent.start.toFixed(2) : 0}s <i class="bi bi-arrow-right"></i> {selectedEvent.end ? selectedEvent.end.toFixed(2) : 0 }s</span></p>
                <p class="small text-muted mb-0">{m.summary_smpte_label()}: <span class="font-monospace">{tcLib.secToTc(selectedEvent.start, projectState.frameRate, projectState.dropFrame)} <i class="bi bi-arrow-right"></i> {tcLib.secToTc(selectedEvent.end, projectState.frameRate, projectState.dropFrame)}</span></p>
                <p class="small text-muted">{m.summary_clock_label()}: <span class="font-monospace">{tcLib.secToTcMs(selectedEvent.start, projectState.frameRate, projectState.dropFrame)} <i class="bi bi-arrow-right"></i> {tcLib.secToTcMs(selectedEvent.end, projectState.frameRate, projectState.dropFrame)}</span></p>
            </div>
        </div>
    {:else}
        <div class="text-center text-muted py-2">
            <i class="bi bi-info-circle me-2"></i>
            <small>{m.summary_no_event_selected()}</small>
        </div>
    {/if}
</div>
{/key}

<style>
    #maxReadingRateMarkerLine {
        position: absolute;
        left: 75%;
        top: 0;
        bottom: 0;
        width: 2px;
        background-color: rgba(255, 255, 255, 0.8);
        box-shadow: 0 0 4px rgba(0, 0, 0, 0.5);
        z-index: 10;
        pointer-events: none;
    }

    #ReadingRateMeter {
        position: relative;
    }
</style>
